import React from 'react';
import { Document, Image, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { FONT, PAPER_SIZE } from '../../types/enums';
import { VisaDocumentProps } from '../types';
import PDFUtils from '../../badge/utils/pdfUtils';
import moment from 'moment';
import 'moment/locale/en-gb';
import { getFieldWithEnding } from './utils';
import { registerFonts } from '../../badge/pdf/pdfDocument/pdfStyles';
import { TextWithFontCheck } from '../../common/components/textWithFontCheck/TextWithFontCheck';
import { useVisaDocumentFR } from './hooks/useVisaDocumentFR';

const styles = StyleSheet.create({
  container: {
    fontSize: '11px',
    fontFamily: FONT.HELVETICA,
    paddingLeft: '3cm',
    paddingRight: '3cm',
    paddingBottom: '.5cm'
  },
  footer: {
    minWidth: '596px',
    maxWidth: '596px',
    minHeight: '172px',
    maxHeight: '172px',
    marginTop: 'auto'
  },
  logo: {
    minWidth: '596px',
    maxWidth: '596px',
    minHeight: '86px',
    maxHeight: '86px',
    marginBottom: '10px'
  },
  bold: {
    fontFamily: FONT.HELVETICA_BOLD
  },
  personalInfo: {
    marginTop: 10
  },
  exhibitor: {
    width: '100%',
    textAlign: 'right',
    fontSize: '14px'
  },
  appeal: {
    marginBottom: 10
  },
  appeal1: {
    marginBottom: 10
  },
  explanation: {
    marginBottom: 10
  },
  explanationEnd: {
    marginTop: 8
  },
  personalInfo1: {
    marginTop: 10
  },
  permissionsList: {
    marginLeft: 20,
    marginBottom: 10,
    marginTop: 10
  },
  personalInfo2: {
    marginTop: 20,
    marginBottom: 10
  }
});

export const VisaDocumentENGBExhibitor: React.FC<VisaDocumentProps> = ({
                                                                         event, person, company, logoSrc, footerSrc
                                                                       }) => {
  registerFonts();
  moment.locale('en');
  const { startDate, endDate } = useVisaDocumentFR(event.dates);

  const eventState = getFieldWithEnding(event.state, ', ');
  const eventCity = getFieldWithEnding(event.city, ', ');
  const eventCountry = getFieldWithEnding(event.country);

  return <Document>
    <Page size={PAPER_SIZE.A4}>
      <Image style={styles.logo} src={PDFUtils.returnSrc(logoSrc)} />
      <View style={styles.container}>
        <View style={[styles.exhibitor, styles.bold]}>
          <Text>EXHIBITOR</Text>
        </View>
        <View style={styles.personalInfo}>
          <Text>{moment().format('DD MMM YYYY')}</Text>
          <View style={styles.personalInfo1}>
            <TextWithFontCheck>{person.passportData.name}</TextWithFontCheck>
            <Text>
              <TextWithFontCheck>{company.name}</TextWithFontCheck> (“Company”)
            </Text>
            <TextWithFontCheck>{person.position}</TextWithFontCheck>
            <TextWithFontCheck>{company.address1}</TextWithFontCheck>
            {(company.address2 && <TextWithFontCheck>{company.address2}</TextWithFontCheck>) || null}
            <TextWithFontCheck>{company.city}</TextWithFontCheck>
            <TextWithFontCheck>{company.state}</TextWithFontCheck>
            <TextWithFontCheck>{company.postCode}</TextWithFontCheck>
            <TextWithFontCheck>{company.country}</TextWithFontCheck>
          </View>
          <View style={styles.personalInfo2}>
            <Text style={styles.bold}>Visa Application for: <TextWithFontCheck bold
              style={styles.bold}>{person.passportData.name}</TextWithFontCheck></Text>
            <Text style={styles.bold}>Date of Birth:{' '}
              <TextWithFontCheck
                style={styles.bold} bold>{moment(person.passportData.dateOfBirth).format('DD MMMM YYYY')}</TextWithFontCheck>
            </Text>
            <Text style={styles.bold}>Passport Number: <TextWithFontCheck bold
              style={styles.bold}>{person.passportData.passportNumber}</TextWithFontCheck></Text>
          </View>
        </View>
        <View style={styles.appeal}>
          <Text style={styles.appeal1}>Dear Sir/Madam:</Text>
          <Text>You have expressed interest in attending <TextWithFontCheck
            bold
            style={styles.bold}>{event.name}</TextWithFontCheck> (the “Event”), which is
            managed by <TextWithFontCheck bold style={styles.bold}>{event.rxLegalEntity}</TextWithFontCheck>
            {' '}(“RX”) in <TextWithFontCheck bold style={styles.bold}>{event.country}</TextWithFontCheck>. RX, the world’s
            leading organizer of trade and
            consumer
            exhibitions, runs over 500 events
            in almost 30 countries, attracting more than 7,000,000 participants. Your attendance at
            the <TextWithFontCheck
              style={styles.bold} bold>{event.name}</TextWithFontCheck> would be
            as a representative of the Company named above, who will be exhibiting at the Event. The Event is scheduled
            to
            be held at the <TextWithFontCheck bold style={styles.bold}>{event.venue}</TextWithFontCheck> in <Text
              style={styles.bold}><TextWithFontCheck bold
              style={styles.bold}>{eventCity}</TextWithFontCheck><TextWithFontCheck
              style={styles.bold} bold>{eventState}</TextWithFontCheck><TextWithFontCheck
              style={styles.bold} bold>{eventCountry}</TextWithFontCheck></Text>, on <Text
              style={styles.bold}>{startDate} - {endDate}</Text> and RX
            would like you to be
            there.</Text>
        </View>
        <View>
          <Text>This invitation involves the following:</Text>
          <View style={styles.permissionsList}>
            <Text>1) The trip from <TextWithFontCheck
              style={styles.bold} bold>{person.passportData.country}</TextWithFontCheck> to <TextWithFontCheck
              style={styles.bold} bold>{eventCity}</TextWithFontCheck><TextWithFontCheck
              style={styles.bold} bold>{eventState}</TextWithFontCheck><TextWithFontCheck
              style={styles.bold} bold>{eventCountry}</TextWithFontCheck></Text>
            <Text>2) The Company named above, will be solely responsible for paying for all costs of your trip (plane,
              hotel, etc.).</Text>
            <Text>3) The show days are <Text style={styles.bold}>{startDate} - {endDate}</Text> with additional dates
              needed before
              and
              after the Event for the
              installation and dismantling of booths, as well as meetings with customers.</Text>
          </View>
        </View>
        <View>
          <Text style={styles.explanation}>Please note that while RX would like you to attend <TextWithFontCheck
            style={styles.bold} bold>{event.name}</TextWithFontCheck>, it is in the
            sole discretion of the <TextWithFontCheck bold style={styles.bold}>{event.country}</TextWithFontCheck> government
            to determine whether or
            not to issue you a visa to enter <TextWithFontCheck
              style={styles.bold} bold>{event.country}</TextWithFontCheck>. For more information on the Event,
            please access the official website at <TextWithFontCheck>{event.websiteUrl}</TextWithFontCheck>. </Text>
          <Text>Sincerely,</Text>
          <Text style={styles.explanationEnd}><TextWithFontCheck>{event.name}</TextWithFontCheck> Management</Text>
        </View>
      </View>
      <Image style={styles.footer} src={PDFUtils.returnSrc(footerSrc)} />
    </Page>
  </Document>;
};
